import React, { useContext, useState } from "react";
import CacheBuster from "react-cache-buster";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import packageJson from "../package.json";
import { DataProvider, DataContext } from "./context/DataContext";
import { DocumentEntity, VideoEntity, VimeoVideo } from "./types/strapi";
import "./i18n";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./index.scss";
import Home from "./elements/Home";
import About from "./elements/About";
import Archive from "./elements/Archive";
import ArchiveDetails from "./elements/ArchiveDetails";
import Cases from "./elements/Cases";
import CaseDetails from "./elements/CaseDetails";
import Evidence from "./elements/Evidence";
import GraphLab from "./elements/graphlab/GraphLab";
import Contact from "./elements/Contact";
import Contributors from "./elements/about/Contributors";
import Error404 from "./elements/error404";
import ErrorBoundary from "./elements/common/ErrorBoundary";

const { version } = packageJson;

// Wrapper component to handle useParams for ArchiveDetails
interface ArchiveDetailsWrapperProps {
  archiveCollectionName?: string | null;
  archiveCollectionItemId?: string | null;
  documents?: DocumentEntity[];
  setArchiveCollectionName?: (name: string | null) => void;
  setRedirectToArchive?: (redirect: boolean) => void;
  videos?: VideoEntity[];
  videosVimeo?: VimeoVideo[];
}

const ArchiveDetailsWrapper: React.FC<ArchiveDetailsWrapperProps> = (props) => {
  const { urlslug } = useParams<{ urlslug: string }>();
  return <ArchiveDetails {...props} urlSlug={urlslug} />;
};

const App: React.FC = () => {
  const dataContext = useContext(DataContext);

  if (!dataContext) {
    throw new Error("App must be wrapped with DataProvider");
  }

  const {
    about,
    archiveCollectionItemId,
    archiveCollectionName,
    backgroundImages,
    cases,
    contact,
    contributors,
    documents,
    home,
    setArchiveCollectionName,
    sponsors,
    videos,
    videosVimeo,
  } = dataContext;

  const [redirectToArchive, setRedirectToArchive] = useState(false);
  const [redirectToCases, setRedirectToCases] = useState(false);

  // Handle redirects
  if (redirectToArchive) {
    return (
      <ErrorBoundary>
        <BrowserRouter basename="/">
          <Routes>
            <Route path="*" element={<Navigate to="/archive" replace />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    );
  }

  if (redirectToCases) {
    return (
      <ErrorBoundary>
        <BrowserRouter basename="/">
          <Routes>
            <Route path="*" element={<Navigate to="/cases" replace />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <BrowserRouter basename="/">
        <Routes>
          <Route path="/" element={<Home home={home} />} />
          <Route
            path="/about"
            element={
              <About
                about={about}
                backgroundImages={backgroundImages}
                sponsors={sponsors}
              />
            }
          />
          <Route
            path="/about/contributors"
            element={
              <Contributors contributors={contributors} sponsors={sponsors} />
            }
          />
          <Route path="/cases" element={<Cases cases={cases} />} />
          <Route
            path="/cases/:urlslug"
            element={<CaseDetails setRedirectToCases={setRedirectToCases} />}
          />
          <Route
            path="/contact"
            element={<Contact contact={contact} sponsors={sponsors} />}
          />
          <Route
            path="/archive"
            element={
              <Archive
                backgroundImages={backgroundImages}
                documents={documents}
                sponsors={sponsors}
                videos={videos}
              />
            }
          />
          <Route
            path="/archive/:urlslug"
            element={
              <ArchiveDetailsWrapper
                archiveCollectionName={archiveCollectionName}
                archiveCollectionItemId={archiveCollectionItemId}
                documents={documents}
                setArchiveCollectionName={setArchiveCollectionName}
                setRedirectToArchive={setRedirectToArchive}
                videos={videos}
                videosVimeo={videosVimeo}
              />
            }
          />

          <Route path="/evidence" element={<Evidence />} />
          <Route path="/evidence/graph" element={<GraphLab />} />
          <Route
            path="/evidence/graph-lab"
            element={<Navigate to="/evidence/graph" replace />}
          />

          <Route path="/404" element={<Error404 />} />
          <Route path="*" element={<Error404 />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const root = createRoot(rootElement);
root.render(
  <CacheBuster
    currentVersion={version}
    isEnabled
    isVerboseMode={false}
    metaFileDirectory="."
    onCacheClear={() => window.location.reload()}
  >
    <DataProvider>
      <App />
    </DataProvider>
  </CacheBuster>
);
